import { BrowserModule } from '@angular/platform-browser';
import { NgModule, ErrorHandler } from '@angular/core';
import { AppRoutingModule } from './app-routing.module';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { providePrimeNG } from 'primeng/config';
import {
  Atomic,
  AvatarComponent,
  HashPipe,
  InteractiveToastComponent,
  InitialsPipe,
  LayoutComponent,
  ListItemComponent,
  MiMenuComponent,
  NavigationComponent,
  RoundButtonComponent,
  ScrollContainerComponent,
  TwoPaneLayoutComponent
} from '@mitel-internal/primeng-lib';
import { AutoCompleteModule } from 'primeng/autocomplete';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { ConfirmationService, MessageService } from 'primeng/api';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { InputTextModule } from 'primeng/inputtext';
import { MessageModule } from 'primeng/message';
import { TabsModule } from 'primeng/tabs';
import { TextareaModule } from 'primeng/textarea';
import { TooltipModule } from 'primeng/tooltip';
import { ToolbarModule } from 'primeng/toolbar';
import { DynamicDialogModule } from 'primeng/dynamicdialog';
import { DialogService } from 'primeng/dynamicdialog';

import { AppComponent } from './app.component';

import { CloudlinkComponentsChatModule, version } from '@mitel-internal/cloudlink-components-chat';
import { TranslateModule, TranslateLoader } from '@ngx-translate/core';
import { MultiTranslateHttpLoader } from 'ngx-translate-multi-http-loader';
import { HttpBackend, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { registerLocaleData, LocationStrategy, HashLocationStrategy } from '@angular/common';
import localeCy from '@angular/common/locales/cy';
import localeDa from '@angular/common/locales/da';
import localeDe from '@angular/common/locales/de';
import localeEn from '@angular/common/locales/en';
import localeEnAu from '@angular/common/locales/en-AU';
import localeEnCa from '@angular/common/locales/en-CA';
import localeEnGb from '@angular/common/locales/en-GB';
import localeEs from '@angular/common/locales/es';
import localeEsCl from '@angular/common/locales/es-CL';
import localeEsMx from '@angular/common/locales/es-MX';
import localeFi from '@angular/common/locales/fi';
import localeFr from '@angular/common/locales/fr';
import localeFrCa from '@angular/common/locales/fr-CA';
import localeIt from '@angular/common/locales/it';
import localeNb from '@angular/common/locales/nb';
import localeNl from '@angular/common/locales/nl';
import localePl from '@angular/common/locales/pl';
import localePt from '@angular/common/locales/pt';
import localePtPt from '@angular/common/locales/pt-PT';
import localeRu from '@angular/common/locales/ru';
import localeSv from '@angular/common/locales/sv';
import localeZh from '@angular/common/locales/zh';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ChatPageComponent } from './components/chat-page/chat-page.component';
import { FormModalComponent } from './components/form-modal/form-modal.component';
import { ConversationListComponent } from './components/conversation-list/conversation-list.component';
import { NewConversationDialogComponent } from './components/new-conversation-dialog/new-conversation-dialog.component';
import { InfoTestPageComponent } from './components/info-test-page/info-test-page.component';
import { InfoTestPageTwoComponent } from './components/info-test-page-two/info-test-page-two.component';
import { ServiceWorkerModule } from '@angular/service-worker';
import { environment } from '../environments/environment';
import { ChatErrorHandler } from './chat-error-handler';
import { EmptyComponent } from './components/empty/empty.component';

registerLocaleData(localeCy);
registerLocaleData(localeDa);
registerLocaleData(localeDe);
registerLocaleData(localeEn);
registerLocaleData(localeEnAu);
registerLocaleData(localeEnCa);
registerLocaleData(localeEnGb);
registerLocaleData(localeEs);
registerLocaleData(localeEsCl);
registerLocaleData(localeEsMx);
registerLocaleData(localeFi);
registerLocaleData(localeFr);
registerLocaleData(localeFrCa);
registerLocaleData(localeIt);
registerLocaleData(localeNb);
registerLocaleData(localeNl);
registerLocaleData(localePl);
registerLocaleData(localePt);
registerLocaleData(localePtPt);
registerLocaleData(localeRu);
registerLocaleData(localeSv);
registerLocaleData(localeZh);

const createTranslateLoader = (http: HttpBackend) =>
  new MultiTranslateHttpLoader(http, [{ prefix: 'assets/i18n/cl-chat/', suffix: '.json?cache=' + version }]);

@NgModule({
  declarations: [
    AppComponent,
    ChatPageComponent,
    FormModalComponent,
    ConversationListComponent,
    NewConversationDialogComponent,
    InfoTestPageComponent,
    InfoTestPageTwoComponent,
    EmptyComponent
  ],
  bootstrap: [AppComponent],
  imports: [
    ReactiveFormsModule,
    BrowserModule,
    AppRoutingModule,
    FormsModule,
    CloudlinkComponentsChatModule,
    AutoCompleteModule,
    ButtonModule,
    CheckboxModule,
    ConfirmDialogModule,
    InputTextModule,
    MessageModule,
    TabsModule,
    TextareaModule,
    TooltipModule,
    ToolbarModule,
    DynamicDialogModule,
    AvatarComponent,
    HashPipe,
    InteractiveToastComponent,
    InitialsPipe,
    LayoutComponent,
    ListItemComponent,
    MiMenuComponent,
    NavigationComponent,
    RoundButtonComponent,
    ScrollContainerComponent,
    TwoPaneLayoutComponent,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: createTranslateLoader,
        deps: [HttpBackend]
      }
    }),
    ServiceWorkerModule.register('ngsw-worker.js', {
      enabled: environment.production
    })
  ],
  providers: [
    { provide: LocationStrategy, useClass: HashLocationStrategy },
    { provide: ErrorHandler, useClass: ChatErrorHandler },
    { provide: 'cloudlinkExpiryTime', useValue: 86400000 },
    { provide: 'cloudlinkRefreshTime', useValue: 21600000 },
    DialogService,
    ConfirmationService,
    // Backs @mitel-internal/primeng-lib NotificationService toasts, rendered by <mi-interactive-toast>.
    MessageService,
    provideHttpClient(withInterceptorsFromDi()),
    provideAnimationsAsync(),
    providePrimeNG({
      theme: {
        preset: Atomic,
        options: {
          prefix: 'p',
          darkModeSelector: '.app-dark',
          cssLayer: {
            name: 'primeng',
            order: 'theme, base, primeng'
          }
        }
      }
    })
  ]
})
export class AppModule {}
